import React from "react";
import { api, formatPHP } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Sun, Moon, Star, Plus } from "lucide-react"; // eslint-disable-line no-unused-vars
import { toast } from "sonner";

// Overnight Use (2 PM → 10 AM) is offered any day; Day Use (9 AM – 5 PM) and
// Night Use (7 PM – 8 AM) are Mondays–Thursdays only.
const TYPES = [
  { key: "whole_day", label: "Overnight Use", icon: Star },
  { key: "day_use", label: "Day Use", icon: Sun },
  { key: "night_use", label: "Night Use", icon: Moon },
];

export default function AdminNewBookingDialog({
  rates = [],
  onCreated,
  defaultDate,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  hideTrigger = false,
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = openProp !== undefined ? openProp : internalOpen;
  const setOpen = (v) => {
    if (onOpenChangeProp) onOpenChangeProp(v);
    if (openProp === undefined) setInternalOpen(v);
  };
  const [submitting, setSubmitting] = React.useState(false);
  const [form, setForm] = React.useState(initialState);

  function initialState() {
    return {
      guest_name: "",
      guest_email: "",
      guest_phone: "",
      num_guests: 1,
      booking_type: "whole_day",
      booking_date: defaultDate || new Date().toISOString().slice(0, 10),
      num_days: 1,
      total_price: 0,
      special_requests: "",
      send_email: true,
    };
  }

  // Re-seed the form whenever the dialog opens (so defaultDate from parent applies)
  React.useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...initialState(),
        booking_type: f.booking_type,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultDate]);

  // Prefill price suggestion from configured rate × num_days when type/days change
  React.useEffect(() => {
    const rate = rates.find((r) => r.type === form.booking_type);
    if (rate) {
      setForm((f) => ({ ...f, total_price: Number(rate.price) * Number(f.num_days || 1) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.booking_type, form.num_days, rates.length]);

  const submit = async () => {
    if (!form.guest_name.trim()) return toast.error("Friend's name is required");
    if (Number(form.total_price) < 0) return toast.error("Price cannot be negative");
    if (Number(form.downpayment_amount) < 0) return toast.error("Downpayment cannot be negative");
    if (Number(form.downpayment_amount) > Number(form.total_price)) {
      return toast.error("Downpayment cannot exceed the total price");
    }
    setSubmitting(true);
    try {
      await api.post("/admin/bookings", {
        ...form,
        num_guests: Number(form.num_guests) || 1,
        num_days: Number(form.num_days) || 1,
        total_price: Number(form.total_price) || 0,
        downpayment_amount: Number(form.downpayment_amount) || 0,
      });
      toast.success("Booking created — guest is auto-confirmed");
      setOpen(false);
      setForm(initialState());
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const fullRate = rates.find((r) => r.type === form.booking_type);
  const fullPrice = fullRate ? Number(fullRate.price) * Number(form.num_days || 1) : 0;
  const discount = fullPrice - Number(form.total_price || 0);
  const discountPct = fullPrice > 0 ? Math.round((discount / fullPrice) * 100) : 0;

  return (
    <>
      {!hideTrigger && (
        <Button
          type="button"
          onClick={() => setOpen(true)}
          className="gold-btn rounded-full px-5"
          data-testid="admin-new-booking-button"
        >
          <Plus size={16} className="mr-1.5" /> New Booking
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0B10] border-[var(--border)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              New <span className="italic text-[#C5A059]">Friend</span> Booking
            </DialogTitle>
            <DialogDescription className="text-[#A1A4B5] text-xs">
              Manually create a confirmed booking with a custom price. No payment proof
              needed. Counts toward Confirmed Revenue.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Guest details */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Guest Name</Label>
                <Input
                  data-testid="new-booking-name"
                  value={form.guest_name}
                  onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                  className="mt-2 bg-[#13151D] border-[var(--border)]"
                  placeholder="Friend's full name"
                />
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Phone</Label>
                <Input
                  data-testid="new-booking-phone"
                  value={form.guest_phone}
                  onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                  className="mt-2 bg-[#13151D] border-[var(--border)]"
                  placeholder="+63 917 …"
                />
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Email (optional)</Label>
                <Input
                  data-testid="new-booking-email"
                  type="email"
                  value={form.guest_email}
                  onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                  className="mt-2 bg-[#13151D] border-[var(--border)]"
                  placeholder="friend@example.com"
                />
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Guests</Label>
                <Input
                  data-testid="new-booking-guests"
                  type="number"
                  min="1"
                  max="50"
                  value={form.num_guests}
                  onChange={(e) => setForm({ ...form, num_guests: e.target.value })}
                  className="mt-2 bg-[#13151D] border-[var(--border)]"
                />
              </div>
            </div>

            {/* Booking type */}
            <div>
              <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Booking Type</Label>
              <div className="grid grid-cols-1 gap-3 mt-2">
                {TYPES.map(({ key, label, icon: Icon }) => {
                  const active = form.booking_type === key;
                  const rate = rates.find((r) => r.type === key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm({ ...form, booking_type: key })}
                      data-testid={`new-booking-type-${key}`}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        active
                          ? "border-[#C5A059] bg-[#C5A059]/10"
                          : "border-[var(--border)] hover:border-[#C5A059]/50"
                      }`}
                    >
                      <Icon size={16} className={active ? "text-[#C5A059]" : "text-[#A1A4B5]"} />
                      <div className="mt-1.5 text-sm font-medium">{label}</div>
                      <div className="text-xs text-[#A1A4B5] mt-0.5">
                        {rate ? `${formatPHP(rate.price)}/day · ${rate.time_window || "2 PM – 10 AM"}` : "—"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date + length */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Start Date</Label>
                <Input
                  data-testid="new-booking-date"
                  type="date"
                  value={form.booking_date}
                  onChange={(e) => setForm({ ...form, booking_date: e.target.value })}
                  className="mt-2 bg-[#13151D] border-[var(--border)]"
                />
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Length of Stay (days)</Label>
                <Input
                  data-testid="new-booking-days"
                  type="number"
                  min="1"
                  max="30"
                  value={form.num_days}
                  onChange={(e) => setForm({ ...form, num_days: e.target.value })}
                  className="mt-2 bg-[#13151D] border-[var(--border)]"
                />
              </div>
            </div>

            {/* Custom price */}
            <div className="rounded-xl border border-[#C5A059]/30 bg-[#C5A059]/5 p-4 space-y-4">
              <div>
                <Label className="text-[#C5A059] text-xs uppercase tracking-[0.18em] font-semibold">
                  Custom Total Price
                </Label>
                <Input
                  data-testid="new-booking-price"
                  type="number"
                  min="0"
                  value={form.total_price}
                  onChange={(e) => setForm({ ...form, total_price: e.target.value })}
                  className="mt-2 bg-[#0A0B10] border-[#C5A059]/40 text-lg"
                />
                {fullPrice > 0 && (
                  <div className="mt-2.5 flex items-center justify-between text-xs">
                    <span className="text-[#A1A4B5]">
                      Standard rate × {form.num_days || 1} days = {formatPHP(fullPrice)}
                    </span>
                    {discount > 0 && (
                      <span className="text-[#C5A059] font-semibold">
                        Discount: {formatPHP(discount)} ({discountPct}% off)
                      </span>
                    )}
                    {discount < 0 && (
                      <span className="text-rose-300 font-semibold">
                        Surcharge: +{formatPHP(-discount)}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-[#C5A059]/20 pt-4">
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  Downpayment <span className="normal-case tracking-normal text-[#A1A4B5]/70">(optional)</span>
                </Label>
                <Input
                  data-testid="new-booking-downpayment"
                  type="number"
                  min="0"
                  value={form.downpayment_amount}
                  onChange={(e) => setForm({ ...form, downpayment_amount: e.target.value })}
                  className="mt-2 bg-[#0A0B10] border-[var(--border)]"
                  placeholder="0"
                />
                {Number(form.downpayment_amount) > 0 && Number(form.total_price) > 0 && (
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-[#A1A4B5]">
                      Balance on arrival:{" "}
                      <span className="text-white">
                        {formatPHP(
                          Math.max(
                            Number(form.total_price) - Number(form.downpayment_amount),
                            0
                          )
                        )}
                      </span>
                    </span>
                    <span className="text-[#C5A059]">
                      {Math.round(
                        (Number(form.downpayment_amount) / Number(form.total_price)) * 100
                      )}
                      % paid
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Notes / Special Requests</Label>
              <Textarea
                data-testid="new-booking-notes"
                value={form.special_requests}
                onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
                className="mt-2 bg-[#13151D] border-[var(--border)]"
                rows={2}
                placeholder="e.g., Friend of the owner, 50% discount applied"
              />
            </div>

            {/* Email opt-in */}
            <label className="flex items-center gap-2 text-xs text-[#A1A4B5] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.send_email}
                onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                className="accent-[#C5A059]"
                data-testid="new-booking-send-email"
              />
              Send confirmation email to guest (only if email is provided)
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="bg-transparent border-[var(--border)]"
                data-testid="new-booking-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="gold-btn rounded-full px-6"
                data-testid="new-booking-submit"
              >
                {submitting ? "Creating…" : "Confirm Booking"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
